import { readApiError } from '../utils/apiError';
import { mainAuth } from './firebase';
import { getIdToken } from 'firebase/auth';
import { functionEndpoint } from './endpoints';

/**
 * Wrapper around fetch that automatically includes authentication headers,
 * handles JSON parsing, and provides standardized error handling.
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Standard fetch options
 * @returns {Promise<any>} - The parsed JSON response body
 */
export async function authenticatedFetch(url, options = {}) {
  const user = mainAuth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await getIdToken(user);
  const emulatedOrg = sessionStorage.getItem('emulatedOrganisation');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
    ...options.headers,
  };

  // Add emulation header if an organization is being emulated
  if (emulatedOrg) {
    headers['X-Emulated-Organisation'] = emulatedOrg;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });
  return response;
}

/**
 * Downloads a response from an authenticated endpoint as a file.
 * Derives the filename from Content-Disposition if present.
 */
export async function downloadFromEndpoint(url, init = {}, fallbackFilename = 'export') {
  const res = await authenticatedFetch(url, init);
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^\"]+)"/i);
  const filename = m ? m[1] : `${fallbackFilename}.${blob.type && blob.type.includes('zip') ? 'zip' : 'xlsx'}`;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
/**
 * Helper function to build query strings from an object
 * @param {object} params - Object containing query parameters
 * @returns {string} - Query string including the leading '?'
 */
export function buildQueryString(params) {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v));
      } else {
        searchParams.append(key, value);
      }
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
} 

/**
 * Securely fetches a GCS object by first obtaining a signed URL from the backend.
 * @param {string} gcsPath - The full gs:// path to the object.
 * @returns {Promise<any>} - The parsed JSON content of the GCS object.
 */
export const fetchGcsObject = (gcsPath) => withDownloadRetry(() => fetchGcsObjectOnce(gcsPath));

const fetchGcsObjectOnce = async (gcsPath) => {
  if (!gcsPath) {
    console.error("fetchGcsObject called with no gcsPath");
    return null;
  }
  try {
    const getSignedUrlEndpoint = functionEndpoint('get_signed_gcs_url');
    
    // Step 1: Get the raw Response object from our backend function.
    const response = await authenticatedFetch(getSignedUrlEndpoint, {
      method: 'POST',
      body: JSON.stringify({ gcsPath }),
    });

    // Step 2: Check if the request for the signed URL was successful.
    if (!response.ok) {
        throw new Error(await readApiError(response, `Failed to get signed URL: ${response.status}`));
    }

    // Step 3: Parse the JSON body of the successful response.
    const responseJson = await response.json();
    const signedUrl = responseJson.signedUrl;

    if (!signedUrl) {
      throw new Error("Backend response did not contain a signedUrl.");
    }

    // Step 4: Use a standard, unauthenticated fetch to download the content directly from GCS.
    const gcsResponse = await fetch(signedUrl);
    if (!gcsResponse.ok) {
      throw new Error(`GCS download failed with status: ${gcsResponse.status}`);
    }
    return await gcsResponse.json();
  } catch (error) {
    console.error(`Failed to fetch GCS object at ${gcsPath}:`, error);
    throw error;
  }
};

/**
 * Returns a signed URL for a given gs:// path.
 * @param {string} gcsPath
 * @param {{signal?: AbortSignal}} options
 * @returns {Promise<string>} signedUrl
 */
export async function getSignedUrl(gcsPath, options = {}) {
  const getSignedUrlEndpoint = functionEndpoint('get_signed_gcs_url');
  const response = await authenticatedFetch(getSignedUrlEndpoint, {
    method: 'POST',
    body: JSON.stringify({ gcsPath }),
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, `Failed to get signed URL: ${response.status}`));
  }
  const { signedUrl } = await response.json();
  if (!signedUrl) throw new Error('Backend response did not contain a signedUrl.');
  return signedUrl;
}

/**
 * Fetches a GCS object as plain text via a signed URL (fetchGcsObject is
 * JSON-only). Signs the path, fetches, and re-signs once on 401/403 —
 * mirroring CanvasPane's stale-signed-URL retry pattern.
 * @param {string} gcsPath
 * @returns {Promise<string>}
 */
export async function fetchGcsText(gcsPath) {
  if (!gcsPath) {
    throw new Error('fetchGcsText called with no gcsPath');
  }
  let signedUrl = await getSignedUrl(gcsPath);
  let response = await fetch(signedUrl);
  if (response.status === 401 || response.status === 403) {
    signedUrl = await getSignedUrl(gcsPath);
    response = await fetch(signedUrl);
  }
  if (!response.ok) {
    throw new Error(`GCS text download failed with status: ${response.status}`);
  }
  return await response.text();
}

// Storage downloads see transient failures — a dropped connection, a 5xx from
// the signed-URL endpoint or the bucket. Every download path retries a whole
// attempt (signing included) a few times before the failure reaches the user.
export const DOWNLOAD_ATTEMPTS = 3;
const DOWNLOAD_RETRY_DELAY_MS = 500;

export async function withDownloadRetry(operation) {
  let lastError;
  for (let attempt = 1; attempt <= DOWNLOAD_ATTEMPTS; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < DOWNLOAD_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_RETRY_DELAY_MS * attempt));
      }
    }
  }
  throw lastError;
}

// The user-facing line for files that could not be fetched after every retry.
// `savedCount` is how many others did make it into the archive, if any.
export const downloadFailureMessage = (failedNames, savedCount = 0) => (
  `We weren't able to download: ${failedNames.join(', ')}`
  + (savedCount > 0
    ? ` — the zip contains the other ${savedCount} file${savedCount === 1 ? '' : 's'}.`
    : '.')
);

/**
 * Fetches a Blob from GCS by prefix via a signed URL, retrying transient failures.
 * @param {string} prefix
 * @returns {Promise<Blob>}
 */
export function fetchGcsBlobByPrefix(prefix) {
  return withDownloadRetry(() => fetchGcsBlobByPrefixOnce(prefix));
}

async function fetchGcsBlobByPrefixOnce(prefix) {
  const bucket = process.env.REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET;
  if (!bucket) throw new Error('Missing REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET');
  const normalizedPrefix = prefix.startsWith('/') ? prefix.slice(1) : prefix;
  const gcsPath = `gs://${bucket}/${normalizedPrefix}`;
  const signedUrl = await getSignedUrl(gcsPath);
  const res = await fetch(signedUrl);
  if (!res.ok) throw new Error(`Blob download failed: ${res.status}`);
  return await res.blob();
}

/**
 * Downloads a GCS object given its bucket-relative prefix/path by first
 * obtaining a signed URL from the backend and then triggering a browser download.
 * The signing request is retried; the transfer itself is the browser's.
 * @param {string} prefix - Bucket-relative path (e.g. users/{company}/tables/{table}/.../file.xlsx)
 * @param {string} [suggestedFilename] - Optional filename to suggest to the browser
 */
export function downloadGcsFileByPrefix(prefix, suggestedFilename) {
  return withDownloadRetry(() => downloadGcsFileByPrefixOnce(prefix, suggestedFilename));
}

async function downloadGcsFileByPrefixOnce(prefix, suggestedFilename) {
  if (!prefix) {
    console.error('downloadGcsFileByPrefix called with no prefix');
    return;
  }

  const bucket = process.env.REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET;
  if (!bucket) {
    throw new Error('Missing REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET');
  }

  const normalizedPrefix = prefix.startsWith('/') ? prefix.slice(1) : prefix;
  const gcsPath = `gs://${bucket}/${normalizedPrefix}`;

  // The stored path is a version token with no name in it, so the caller's
  // suggestion is the only real filename. It goes to the signing endpoint rather
  // than onto the anchor: the browser follows a signed URL cross-origin, where
  // an anchor's download attribute does not apply, and the response header the
  // backend puts on the URL is what names the saved file.
  const filename = suggestedFilename || 'download';
  const getSignedUrlEndpoint = functionEndpoint('get_signed_gcs_url');
  const response = await authenticatedFetch(getSignedUrlEndpoint, {
    method: 'POST',
    body: JSON.stringify({ gcsPath, downloadName: filename }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, `Failed to get signed URL: ${response.status}`));
  }

  const { signedUrl } = await response.json();
  if (!signedUrl) {
    throw new Error('Backend response did not contain a signedUrl.');
  }

  const link = document.createElement('a');
  link.href = signedUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
